import React, { useState } from 'react';
import styled from 'styled-components';
import { FaExclamationTriangle, FaUserEdit, FaCheck } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { pendenciasDoDocumento } from '../others/cadastroCompleto';
import { dispensar, listaDispensados, identificadorDe } from '../others/avisoDispensado';

/**
 * Faixa de aviso: o cliente escolhido para este documento está com o cadastro incompleto.
 *
 * Clientes criados pela importação de CRLV-e nascem só com nome e CPF/CNPJ, e usá-los num documento
 * logo em seguida é o caso comum. O aviso não impede gerar — só garante que ninguém emita sem
 * perceber o que falta, e dá o atalho para completar.
 *
 * - pessoas: [{ rotulo, pessoa }] — o papel de cada um no documento ("Outorgante", "Vendedor"...)
 * - documento: 'procuracao' | 'contrato' | 'documentoComplementar'
 */
function AvisoCadastroIncompleto({ pessoas, documento }) {
  const navigate = useNavigate();
  // A lista de dispensados vive no navegador, mas entra no estado da tela para o React redesenhar
  // sozinho quando alguém dispensa, sem precisar reler o armazenamento a cada render.
  const [dispensados, setDispensados] = useState(listaDispensados);
  const pendentes = pendenciasDoDocumento(pessoas, documento)
    .filter(({ pessoa }) => !dispensados.includes(identificadorDe(pessoa)));

  const naoAvisarMais = (pessoa) => {
    dispensar(pessoa);
    setDispensados(listaDispensados());
  };

  if (pendentes.length === 0) return null;

  // Leva para o cadastro já pesquisando por este cliente (mesmo atalho que a Ordem de Serviço usa)
  const abrirCadastro = (pessoa) => {
    const busca = (pessoa.cpfCnpj || pessoa.nome || '').toString().trim();
    if (busca) localStorage.setItem('clienteSearchTerm', busca);
    navigate('/clientes');
  };

  return (
    <Faixa role="status">
      <FaExclamationTriangle aria-hidden />
      <div className="corpo">
        {pendentes.map(({ rotulo, pessoa, buracos, omitidos }) => (
          <div className="pessoa" key={`${rotulo}-${pessoa.cpfCnpj || pessoa.nome}`}>
            <strong>
              {pessoa.nome || 'Cliente'} <span className="papel">({rotulo})</span> está com o cadastro incompleto.
            </strong>
            {buracos.length > 0 && (
              <span className="buraco">
                Sem estes dados o documento sai com espaço em branco: <b>{buracos.join(', ')}</b>.
              </span>
            )}
            {omitidos.length > 0 && (
              <span className="omitido">
                O documento vai sair sem: <b>{omitidos.join(', ')}</b>.
              </span>
            )}
            <div className="acoes">
              <button type="button" onClick={() => abrirCadastro(pessoa)}>
                <FaUserEdit /> Completar o cadastro
              </button>
              {/* Escritório recém-migrado tem todos os clientes sem endereço: sem poder dispensar, o
                  aviso apareceria sempre e viraria ruído que ninguém lê */}
              <button type="button" className="discreto" onClick={() => naoAvisarMais(pessoa)}>
                <FaCheck /> Entendi, não avisar mais para este cliente
              </button>
            </div>
          </div>
        ))}
      </div>
    </Faixa>
  );
}

const Faixa = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  margin: 12px 0;
  padding: 12px 14px;
  border: 1px solid #f0c36d;
  border-left: 4px solid #e67e22;
  border-radius: 8px;
  background: #fff8e1;
  color: #7a5a00;

  > svg { margin-top: 2px; flex-shrink: 0; color: #e67e22; }
  .corpo { display: flex; flex-direction: column; gap: 12px; flex: 1; min-width: 0; }
  .pessoa { display: flex; flex-direction: column; gap: 3px; }
  strong { font-size: 13.5px; }
  .papel { font-weight: 400; }
  span { font-size: 12.5px; }
  .buraco b { color: #a05000; }
  .omitido { color: #8a6d3b; }

  .acoes { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 4px; }
  button {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: 1px solid #e67e22;
    color: #a05000;
    border-radius: 6px;
    padding: 5px 10px;
    font-size: 12.5px;
    cursor: pointer;
  }
  button:hover { background: #fdf0d5; }
  button.discreto { border-color: #d8c9a8; color: #8a6d3b; }
  button.discreto:hover { background: #fbf3e2; }

  @media print { display: none; }
`;

export default AvisoCadastroIncompleto;
